/// <reference types="google.maps" />

import { canUseFieldNotebook } from 'app/utils/field-notebook-access.utils';
import { FieldNotebookEntry } from 'app/core/interface/field-notebook';

import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChildren,
  inject,
} from '@angular/core';
import { UserTalhaoListService } from 'app/core/services/user-talhao-list.service';
import { MapsLoaderService } from 'app/core/services/maps-loader.service';
import { fromEvent, merge, Observable, Subject } from 'rxjs';
import { Field } from '../../../../core/interface/entity';
import { TranslateService } from '@ngx-translate/core';
import {
  UntypedFormBuilder,
  FormControlName,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { GenericValidator } from 'app/utils/generic-form-validator';
import { PrevisaoTempoService } from 'app/core/services/previsao-tempo.service';
import { AgresSpinnerService } from 'app/shared/components/agres-spinner/agres-spinner.service';
import { FeatureCollection } from 'isoxml';
import { AnalyticsService } from 'app/core/services/analytics.service';
import { AuthStateService } from 'app/core/services/auth-state.service';
import { Geometry } from '@turf/turf';
import { zip } from '@mapbox/shp-write';
import { UserFilesManager } from 'app/utils/user-files-manager';
import { FileManagerService } from 'app/core/services/file-manager.service';
import { unzipFiles } from 'app/utils/zip-manager';
import { normalizeString } from 'app/utils/string.utils';
import { ToastrService } from 'ngx-toastr';

@Component({
  standalone: false,
  selector: 'app-editar-talhoes',
  templateUrl: './editar-talhoes.component.html',
  styleUrls: ['./editar-talhoes.component.scss'],
})
export class EditarTalhoesComponent implements OnInit, AfterViewInit {
  offCanvasOne = new Subject<boolean>();
  talhaoById = [];
  talhaoName = '';
  talhaoDescription = '';
  cityName = '';

  loading: boolean = true;

  mapPolygonColorSubject: Subject<string> = new Subject();
  updateIsPolygonClosed: Subject<boolean> = new Subject();

  selectedColor = '#000';

  @Input() emitEventEditarTalhaoById;
  @Input() onEmitVoltarParaListaDeTalhoes;
  @Input() talhaoId: string | null;

  fetchFieldData: Subject<boolean> = new Subject();

  @ViewChildren(FormControlName, { read: ElementRef })
  formInputElements: ElementRef[];

  isPolygonClosed = false;
  isEditingPolygon = true;
  geometry: FeatureCollection<any> = {
    features: [
      {
        geometry: {
          coordinates: [[]],
          type: 'Polygon',
        },
        properties: {},
        type: 'Feature',
      },
    ],
    type: 'FeatureCollection',
  };

  get notebookEnabled(): boolean {
    return canUseFieldNotebook(this.authState.getUser()?.id);
  }

  parcelReference = '';
  notebookCrop: FieldNotebookEntry['crop'];
  polygons: google.maps.LatLngLiteral[][] = [];

  showPolygon = false;

  apiLoaded: Observable<boolean>;

  vertices: google.maps.LatLngLiteral[] = [];

  mapOptions: google.maps.MapOptions = { draggable: false };

  markerPositions: google.maps.LatLngLiteral[] = [];
  editFieldForm: UntypedFormGroup;
  public validationMessages: { [key: string]: { [key: string]: string } };
  public genericValidator: GenericValidator;
  public displayMessage: { [key: string]: string } = {};
  userId: string;
  fieldId: any;

  private readonly mapsLoader = inject(MapsLoaderService);
  private readonly authState = inject(AuthStateService);
  private readonly cloudantManager: UserFilesManager;

  constructor(
    httpClient: HttpClient,
    public previsaoTempoService: PrevisaoTempoService,
    public router: Router,
    public userTalhoesService: UserTalhaoListService,
    public translateService: TranslateService,
    private readonly toastr: ToastrService,
    public formBuilder: UntypedFormBuilder,
    public loadingAgresSpinner: AgresSpinnerService,
    public readonly analyticsService: AnalyticsService,
    private readonly activeRoute: ActivatedRoute,
    public fileManagerService: FileManagerService,
  ) {
    this.cloudantManager = new UserFilesManager(fileManagerService, httpClient);
    const usuarioLogado = this.authState.getUser();
    this.userId = usuarioLogado?.id;
    this.analyticsService.trackEvent('Accessed the edit field', 'Edit field');
    this.validationMessages = {
      name: {
        required: '',
      },
      description: {},
    };
    this.populateValidateObj();
    this.genericValidator = new GenericValidator(this.validationMessages);
    this.apiLoaded = this.mapsLoader.load();
  }

  ngOnInit(): void {
    this.editFieldForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      area: [''],
      description: [''],
    });
    this.activeRoute.params.subscribe((params) => {
      this.fieldId = params['fieldId'];

      if (this.fieldId) {
        this.userTalhoesService.getTalhaoId(this.fieldId).subscribe(
          (talhoes: Field) => {
            this.parcelReference = talhoes.parcelReference ?? '';
            this.notebookCrop = talhoes.crop;
            const fieldName = talhoes.name;
            const fieldDescription = talhoes.description;
            const fieldArea = (+talhoes.totalArea).toFixed(3);
            this.editFieldForm.controls.area.setValue(fieldArea);
            this.talhaoName = fieldName;
            this.editFieldForm.controls.name.setValue(fieldName);
            this.talhaoDescription = fieldDescription;
            this.editFieldForm.controls.description.setValue(fieldDescription);
            this.geometry = talhoes.geometry;
            this.selectedColor = talhoes.color;
            this.loading = false;
            const [lng, lat] =
              this.geometry.features[0].geometry.coordinates[0][0];
            this.getCityName({ lng, lat });
          },
          (err) => {
            this.translateService
              .get('global.error')
              .subscribe((res: string) => {
                this.toastr.info(res, '', {
                  positionClass: 'toast-top-full-width',
                  disableTimeOut: false,
                });
              });
          },
        );
      }
    });
  }

  ngAfterViewInit() {
    const controlBlurs: Observable<any>[] = this.formInputElements.map(
      (formControl: ElementRef) => fromEvent(formControl.nativeElement, 'blur'),
    );

    merge(...controlBlurs).subscribe((value) => {
      this.displayMessage = this.genericValidator.processMessages(
        this.editFieldForm,
      );
    });
  }

  populateValidateObj() {
    this.translateService
      .get('field-register.validate.name.required')
      .subscribe((res: string) => {
        this.validationMessages.name.required = res;
      });
  }

  openInfomationsOfField() {
    if (this.isPolygonClosed) {
      this.isEditingPolygon = !this.isEditingPolygon;
      this.offCanvasOne.next(true);
    }
  }

  callTogglePolygonDrawing() {
    this.updateIsPolygonClosed.next(false);
    this.isEditingPolygon = true;
    this.isPolygonClosed = false;
  }

  togglePolygonEditButtonTitle(): string {
    if (
      (this.geometry.features[0].geometry as Geometry).coordinates.length > 0
    ) {
      return this.translateService.instant('fields.title-button-draw-the-hole');
    }
    return this.translateService.instant('fields.title-button-draw-the-field');
  }

  hideOffCanvas(): void {
    this.offCanvasOne.next(false);
  }

  cancelEditPolygons(): void {
    this.polygons = [];
    this.markerPositions = [];
    window.location.reload();
  }

  clearPolygons() {
    (this.geometry.features[0].geometry as Geometry).coordinates = [];
    this.isEditingPolygon = true;
    this.isPolygonClosed = false;
    this.fetchFieldData.next(true);
    this.editFieldForm.controls.area.setValue(0);
    this.updateIsPolygonClosed.next(false);
  }

  salvePolygons() {
    const notebookAttributes: {
      parcelReference?: string;
      crop?: FieldNotebookEntry['crop'];
    } = {};
    if (this.notebookEnabled) {
      notebookAttributes.parcelReference = this.parcelReference;
      notebookAttributes.crop = this.notebookCrop;
    }
    this.userTalhoesService
      .putTalhao({
        data: {
          geometry: this.geometry,
          name: this.editFieldForm.controls.name.value,
          id: this.fieldId,
          color: this.selectedColor,
          description: this.editFieldForm.controls.description.value,
          ...notebookAttributes,
          city: this.cityName,
        },
      })

      .subscribe(
        (talhoes) => {
          if (talhoes) {
            this.createShapefile(talhoes.id);
          } else {
            this.translateService
              .get('global.alert.http-status-code-400')
              .subscribe((res: string) => {
                this.toastr.error(res, '', {
                  positionClass: 'toast-top-full-width',
                  disableTimeOut: false,
                });
                this.router.navigate(['/my-fields/']);
              });
          }
        },
        () => {
          this.translateService
            .get('global.alert.http-status-code-400')
            .subscribe((res: string) => {
              this.toastr.error(res, '', {
                positionClass: 'toast-top-full-width',
                disableTimeOut: false,
              });
              this.router.navigate(['/my-fields/']);
            });
        },
      );
  }

  editField() {
    this.offCanvasOne.next(false);
    this.salvePolygons();
    this.analyticsService.trackEvent(
      'Edited field successfully',
      'Edit_Field_Not_NDVI_Map',
    );
  }

  onUpdatePolygons() {
    return (geometry, polygonStatus: boolean, area?: number) => {
      if (!!area) {
        this.editFieldForm.controls.area.setValue(area.toFixed(3));
      }
      this.isPolygonClosed = polygonStatus;
      this.geometry = geometry;
    };
  }

  selectClick(event) {
    this.mapPolygonColorSubject.next(event.target.value);
    this.selectedColor = event.target.value;
  }

  selectCoresTalhao(event) {
    this.mapPolygonColorSubject.next(event.target.value);
    this.selectedColor = event.target.value;
  }

  getCityName(coordinates: { lat: number; lng: number }) {
    this.previsaoTempoService
      .getLocationName(coordinates.lat, coordinates.lng)
      .subscribe({
        next: (data) => {
          this.cityName = data;
        },
        error: () => {},
      });
  }
  async createShapefile(id: string) {
    const name = normalizeString(this.editFieldForm.controls.name.value);

    const shapefilesZip = await zip<'blob'>(this.geometry, {
      types: {
        polygon: name,
      },
      compression: 'STORE',
      outputType: 'blob',
    });

    const files = await unzipFiles(shapefilesZip, {
      filterExtensions: ['cpg', 'prj'],
    });
    const fileClassification = {
      boundaries: files.map((file) => file.name),
      recommendation_files: [],
      guides_files: [],
    };
    this.cloudantManager
      .saveDataAndUploadUserFiles(
        this.userId,
        files,
        'fields',
        id,
        fileClassification,
      )
      .subscribe(() => {
        this.translateService
          .get('global.alert.http-status-code-200')
          .subscribe((res: string) => {
            this.toastr.success(res, '', {
              positionClass: 'toast-top-full-width',
              disableTimeOut: false,
            });
          });
        this.router.navigate([`/my-fields/ver-talhao/${this.fieldId}`]);
      });
  }
}
